import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { SortableTableHeader, useSortableData } from "../components/ui/sortable-table";
import { CreateDeskModal } from "../components/desks//CreateDeskModal";
import { DeskDetailModal } from "../components/desks//DeskDetailModal";
import { Plus, Search, Users, Building2, Edit, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { deleteDesk, fetchAvailableDesks, fetchDesks } from "../services/crmServices/deskService";
import Swal from "sweetalert2";
import { getFullName, updateFirstLetterToUpperCase } from "../services/common/util";
import { useAuth } from "../hooks/useAuth";

export function Desks() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [desks, setDesks] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDesk, setSelectedDesk] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }
        initPage();
    }, []);

    const initPage = () => {
        setLoading(true);
        fetchAvailableDesks()
            .then((res) => {
                if (res?.data?.length) {
                    setDesks(res.data);
                } else {
                    setDesks([]);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Filter and sort using real desks
    const filteredDesks = desks.filter(
        (desk) =>
            (desk.name && desk.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (desk.id && desk.id.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const { sortedData, sortConfig, handleSort } = useSortableData(filteredDesks, "_id");

    const totalPages = Math.ceil(sortedData.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedDesks = sortedData.slice(startIndex, startIndex + pageSize);

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    // Refresh desks after create
    const handleCreateSuccess = () => {
        fetchDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        setShowCreateModal(false);
    };

    // Refresh desks after edit
    const handleEditSuccess = (updatedDesk) => {
        fetchDesks().then((res) => {
            if (res?.data?.length) {
                setDesks(res.data);
            }
        });
        setShowEditModal(false);
        setSelectedDesk(null);
    };

    const handleViewDesk = (desk) => {
        setSelectedDesk(desk);
        setShowDetailModal(true);
    };

    const handleEditDesk = (desk) => {
        setSelectedDesk(desk);
        setShowEditModal(true);
    };

    const handleDelete = (_id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this desk!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, keep it"
        }).then((result) => {
            if (result.isConfirmed) {
                deleteDesk({ _id }).then((res) => {
                    if (res.status !== 200) {
                        toast.error("Something went wrong while deleting the desk.");
                        return;
                    } else {
                        toast.success("Desk deleted successfully");
                        initPage();
                    }
                });
            }
        });
    };

    const handleBack = () => {
        navigate("/");
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Desks Management</h1>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                    <div className="relative w-full sm:max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by ID or name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    {user.permissions.desks.create && (
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            className="w-full sm:w-auto"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Desk
                        </Button>
                    )}
                </div>
            </div>

            {/* Cards View - Mobile Friendly */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 md:hidden">
                {paginatedDesks.map((desk) => (
                    <Card key={desk.id} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                                    <span onClick={() => handleViewDesk(desk)} className="hover:text-blue-600">
                                        {desk.name}
                                    </span>
                                </div>
                                <Badge variant="outline">{desk._id}</Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                <div className="flex space-x-2 pt-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditDesk(desk)}
                                        className="flex-1"
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDelete(desk._id)}
                                        className="flex-1"
                                    >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Table View - Desktop */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Actions</TableHead>
                            <SortableTableHeader sortKey="name" sortConfig={sortConfig} onSort={handleSort}>
                                Name
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="floor" sortConfig={sortConfig} onSort={handleSort}>
                                Floor
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="floorManager" sortConfig={sortConfig} onSort={handleSort}>
                                Floor Manager
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort}>
                                Manager
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort}>
                                Agents
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="manager" sortConfig={sortConfig} onSort={handleSort}>
                                Leads
                            </SortableTableHeader>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedDesks.map((desk) => (
                            <TableRow key={desk._id}>
                                <TableCell>
                                    <div className="flex space-x-1">
                                        {user.permissions.desks.update && (
                                            <Button size="sm" variant="outline" onClick={() => handleEditDesk(desk)}>
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        )}
                                        {user.permissions.desks.delete && (
                                            <Button size="sm" variant="outline" onClick={() => handleDelete(desk._id)}>
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell
                                    className="font-medium cursor-pointer hover:text-blue-600"
                                    onClick={() => handleViewDesk(desk)}
                                >
                                    {desk.name}
                                </TableCell>
                                <TableCell className="font-medium">
                                    {desk.floor ? updateFirstLetterToUpperCase(desk.floor) : "N/A"}
                                </TableCell>
                                <TableCell className="font-medium">
                                    {desk.floorManager ? getFullName(desk.floorManager) : "-"}
                                </TableCell>
                                <TableCell className="font-medium">
                                    {desk.manager ? getFullName(desk.manager) : "-"}
                                </TableCell>
                                <TableCell className="font-medium">{desk.agents.length}</TableCell>
                                <TableCell className="font-medium">{desk.leads.length}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={handlePrevious} disabled={currentPage === 1}>
                        Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <Button variant="outline" size="sm" onClick={handleNext} disabled={currentPage === totalPages}>
                        Next
                    </Button>
                </div>
            </div>

            {filteredDesks.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-gray-400 text-lg mb-2">😔</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No desks found</h3>
                    <p className="text-gray-500">Try adjusting your search criteria</p>
                </div>
            )}

            <CreateDeskModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
            />

            <DeskDetailModal
                open={showDetailModal}
                onClose={() => {
                    setShowDetailModal(false);
                    setSelectedDesk(null);
                }}
                desk={selectedDesk}
            />

            <CreateDeskModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedDesk(null);
                }}
                onSuccess={handleEditSuccess}
                desk={selectedDesk}
            />
        </div>
    );
}
